import { call, put, select, takeEvery, delay, fork, take, cancel } from 'redux-saga/effects';
import camelcaseKeys from 'camelcase-keys';
import * as types from '../types';
import * as actions from '../actions';
import * as selectors from '../reducers';
import * as authTypes from '@redux/types/auth';
import {
  createSessionService,
  closeSessionService,
  endSessionService,
  batchInsertLogsService,
  getActivityLogsService
} from '../services/activityLogService';
import { store } from '@redux/storeConfig/store';

const FLUSH_INTERVAL_MS = 30000; // 30 seconds

// Create session on login
function* createSession(action: any) {
  const { platform } = action.payload;

  createSessionService(
    { platform },
    {
      successAction: (response: any) => {
        console.log('[ActivityLog] createSession response:', JSON.stringify(response));
        const sessionId = response?.data?.SessionId ?? response?.data?.sessionId;
        console.log('[ActivityLog] Extracted sessionId:', sessionId);
        if (sessionId) {
          store.dispatch(actions.completeCreatingSession(sessionId));
        } else {
          console.warn('[ActivityLog] sessionId is falsy! Full response:', response);
        }
      },
      errorAction: (error: any) => {
        console.error('[ActivityLog] createSession error:', error);
        store.dispatch(actions.failCreatingSession(error));
      }
    }
  );
}

export function* watchCreateSession() {
  yield takeEvery(types.ACTIVITY_SESSION_CREATE_STARTED, createSession);
}

// Close session on logout
function* closeSession(action: any) {
  const { sessionId, status } = action.payload;

  closeSessionService(
    { sessionId, status },
    {
      successAction: () => {
        store.dispatch(actions.completeClosingSession());
      },
      errorAction: (error: any) => {
        store.dispatch(actions.failClosingSession(error));
      }
    }
  );
}

export function* watchCloseSession() {
  yield takeEvery(types.ACTIVITY_SESSION_CLOSE_STARTED, closeSession);
}

// Flush buffer to backend
function* flushBuffer() {
  const sessionId: string | null = yield select(selectors.getSessionId);
  const buffer: types.ActivityLogEntry[] = yield select(selectors.getBuffer);

  if (!sessionId || buffer.length === 0) {
    yield put(actions.completeFlushingLogs(0));
    return;
  }

  // Take a snapshot of current buffer before flushing
  const logsToSend = [...buffer];

  batchInsertLogsService(
    { sessionId, logs: logsToSend },
    {
      successAction: (response: any) => {
        const count = response?.data?.InsertedCount ?? response?.data?.insertedCount ?? 0;
        store.dispatch(actions.completeFlushingLogs(count));
      },
      errorAction: (error: any) => {
        store.dispatch(actions.failFlushingLogs(error));
      }
    }
  );
}

export function* watchFlushBuffer() {
  yield takeEvery(types.ACTIVITY_LOG_FLUSH_STARTED, flushBuffer);
}

// Periodic flush every 30 seconds
function* periodicFlush() {
  while (true) {
    yield delay(FLUSH_INTERVAL_MS);
    const sessionId: string | null = yield select(selectors.getSessionId);
    const buffer: types.ActivityLogEntry[] = yield select(selectors.getBuffer);
    if (sessionId && buffer.length > 0) {
      yield put(actions.startFlushingLogs());
    }
  }
}

// Watch for session creation to start periodic flush, stop on logout.
// Session close (EndedAt) is handled by useNavigationTracker hook directly.
function* watchSessionLifecycle() {
  while (true) {
    yield take(types.ACTIVITY_SESSION_CREATE_COMPLETED);

    // Start periodic flush
    const flushTask = yield fork(periodicFlush);

    // Wait for logout
    yield take(authTypes.AUTHENTICATION_IDENTITY_CLEARED);

    // Stop periodic flush and clear buffer
    yield cancel(flushTask);
    yield put(actions.completeFlushingLogs(0));
  }
}

export { watchSessionLifecycle };

// Fetch activity logs (for audit view)
function* fetchActivityLogs(action: any) {
  const filters = action.payload;

  getActivityLogsService(
    { ...filters },
    {
      successAction: (response: any) => {
        const resultData = (response?.data ?? []).map((result: any) => camelcaseKeys(result));
        store.dispatch(actions.completeFetchingActivityLogs(resultData));
      },
      errorAction: (error: any) => {
        store.dispatch(actions.failFetchingActivityLogs(error));
      }
    }
  );
}

export function* watchFetchActivityLogs() {
  yield takeEvery(types.ACTIVITY_LOGS_FETCH_STARTED, fetchActivityLogs);
}
